import { RowComponentProps } from 'react-window';
import { twJoin } from 'tailwind-merge';

import { ScheduledLessonCard } from '@/components/scheduled-lesson/ScheduledLessonCard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Skeleton } from '@/components/ui/daisy/skeleton';
import {
  WEEK_CALENDAR_HEAD_HEIGHT,
  WeekCalendar,
} from '@/components/ui/daisy/templates/week-calendar';
import { toColumnKey } from '@/components/ui/daisy/templates/week-calendar/weekColumns';
import ScheduledLesson from '@/models/ScheduledLesson';

import { LessonRowProps } from './types';
import moment from 'moment';

const CARD_CLASSNAME = 'h-full w-full';

export default function LessonRow({
  index,
  style,
  weekDays,
  lessonsByColumn,
  ...cellProps
}: RowComponentProps<LessonRowProps>) {
  return (
    <WeekCalendar.Row
      style={{ ...style, top: WEEK_CALENDAR_HEAD_HEIGHT }}
      aria-rowindex={index + 1}
    >
      {weekDays.map(({ date, today }, columnIndex) => (
        <WeekCalendar.Column
          key={toColumnKey(date)}
          aria-colindex={columnIndex + 1}
          today={today}
        >
          <LessonCell
            lesson={lessonsByColumn[columnIndex][index]}
            {...cellProps}
          />
        </WeekCalendar.Column>
      ))}
    </WeekCalendar.Row>
  );
}

type LessonCellProps = {
  lesson?: ScheduledLesson;
} & Pick<LessonRowProps, 'loadingPendingSummary' | 'klassesPendingSummaryMap'>;

const LessonCell = ({
  lesson,
  loadingPendingSummary,
  klassesPendingSummaryMap,
}: LessonCellProps) => {
  if (!lesson) return null;

  const klass = klassesPendingSummaryMap[lesson.klass];

  if (!klass)
    return (
      <ConditionalRenderer condition={loadingPendingSummary}>
        <Skeleton
          data-testid="card-skeleton"
          className={twJoin(CARD_CLASSNAME, 'rounded-2xl')}
        />
      </ConditionalRenderer>
    );

  return (
    <ScheduledLessonCard
      className={CARD_CLASSNAME}
      name={klass.name}
      startDate={moment(lesson.datetime).toDate()}
      endDate={klass.klassEndDate}
      studentsCount={klass.enrollsCount}
      attendancesCount={klass.pendingReportsCount}
      examsCount={klass.pendingExamsCount}
      variant={klass.requiresAttention ? 'caution' : 'default'}
    />
  );
};
